import {
  ComponentStore,
  SIGNATURE_WORDS,
  type ComponentType,
} from './ComponentStore'
import {
  entityGeneration,
  entityIndex,
  makeEntity,
  MAX_ENTITIES,
  NULL_ENTITY,
  type Entity,
} from './Entity'
import { Query, queryKey, type QueryFilter } from './Query'

/** Typed key for a singleton stored in `world.resources`. */
export interface ResourceKey<T> {
  readonly name: string
  /** Phantom field — carries T through the type system only. */
  readonly __type?: T
}

export function defineResource<T>(name: string): ResourceKey<T> {
  return { name }
}

const INITIAL_CAPACITY = 512

/**
 * The world owns entities, their component signatures, component stores,
 * singleton resources and the query cache. It is the only mutable game state;
 * everything else (Three.js scene, DOM, audio graph) is derived output.
 */
export class World {
  /** Bumped on every structural change; queries use it to invalidate caches. */
  structuralVersion = 0

  /** Flat signature bitset: `signatures[index * SIGNATURE_WORDS + word]`. */
  signatures: Uint32Array

  private generations: Uint16Array
  private alive: Uint8Array
  private capacity = INITIAL_CAPACITY
  private nextIndex = 0
  private freeList: number[] = []
  private live: Entity[] = []

  private readonly componentTypes: ComponentType<any>[] = []
  /** Component storage, indexed by component type id. Owned by this world. */
  private readonly stores: Array<ComponentStore<any> | undefined> = []
  private readonly queries = new Map<string, Query>()
  private readonly resourceMap = new Map<string, unknown>()
  private readonly pendingDestroy: Entity[] = []

  constructor() {
    this.signatures = new Uint32Array(this.capacity * SIGNATURE_WORDS)
    this.generations = new Uint16Array(this.capacity)
    this.alive = new Uint8Array(this.capacity)
  }

  // ---------------------------------------------------------------- entities

  createEntity(): Entity {
    let index: number
    if (this.freeList.length > 0) {
      index = this.freeList.pop()!
    } else {
      if (this.nextIndex >= MAX_ENTITIES) {
        throw new Error('Entity limit reached')
      }
      index = this.nextIndex++
      if (index >= this.capacity) this.grow(index + 1)
    }

    this.alive[index] = 1
    const e = makeEntity(index, this.generations[index])
    this.live.push(e)
    this.structuralVersion++
    return e
  }

  isAlive(e: Entity): boolean {
    if (e === NULL_ENTITY) return false
    const i = entityIndex(e)
    return (
      i < this.capacity &&
      this.alive[i] === 1 &&
      this.generations[i] === entityGeneration(e)
    )
  }

  /**
   * Queues destruction. Removal happens in `flushDestroyed()`, which the
   * scheduler calls between stages — so a system can safely destroy entities
   * while iterating a query snapshot.
   */
  destroyEntity(e: Entity): void {
    if (!this.isAlive(e)) return
    this.alive[entityIndex(e)] = 2 // "dying": fails isAlive, not yet recycled
    this.pendingDestroy.push(e)
    this.structuralVersion++
  }

  flushDestroyed(): void {
    if (this.pendingDestroy.length === 0) return
    for (const e of this.pendingDestroy) {
      const index = entityIndex(e)
      if (this.alive[index] !== 2) continue

      const base = index * SIGNATURE_WORDS
      for (const type of this.componentTypes) {
        if (this.signatures[base + type.word] & type.mask) this.store(type).remove(e)
      }
      for (let w = 0; w < SIGNATURE_WORDS; w++) this.signatures[base + w] = 0

      this.alive[index] = 0
      this.generations[index] = (this.generations[index] + 1) & 0xfff
      this.freeList.push(index)

      const at = this.live.indexOf(e)
      if (at !== -1) {
        this.live[at] = this.live[this.live.length - 1]
        this.live.pop()
      }
    }
    this.pendingDestroy.length = 0
    this.structuralVersion++
  }

  liveEntities(): readonly Entity[] {
    return this.live
  }

  get entityCount(): number {
    return this.live.length
  }

  // -------------------------------------------------------------- components

  /**
   * The storage for one component type in this world, created on first use.
   * Exposed because a few hot paths iterate the dense array directly.
   */
  store<T>(type: ComponentType<T>): ComponentStore<T> {
    let store = this.stores[type.id] as ComponentStore<T> | undefined
    if (!store) {
      store = new ComponentStore<T>()
      this.stores[type.id] = store
      this.componentTypes.push(type)
    }
    return store
  }

  private registerType(type: ComponentType<any>): void {
    this.store(type)
  }

  /** Adds (or overwrites) a component. Patch fields are merged over defaults. */
  add<T>(e: Entity, type: ComponentType<T>, value?: Partial<T>): T {
    if (!this.isAlive(e)) throw new Error(`add(${type.name}) on dead entity`)
    const store = this.store(type)

    const existing = store.get(e)
    if (existing !== undefined) {
      if (value) Object.assign(existing as object, value)
      return existing
    }

    const data = type.create()
    if (value) Object.assign(data as object, value)
    store.set(e, data)
    this.signatures[entityIndex(e) * SIGNATURE_WORDS + type.word] |= type.mask
    this.structuralVersion++
    return data
  }

  addTag(e: Entity, type: ComponentType<true>): void {
    this.add(e, type)
  }

  remove<T>(e: Entity, type: ComponentType<T>): void {
    if (!this.store(type).remove(e)) return
    this.signatures[entityIndex(e) * SIGNATURE_WORDS + type.word] &= ~type.mask
    this.structuralVersion++
  }

  has<T>(e: Entity, type: ComponentType<T>): boolean {
    const i = entityIndex(e)
    if (i >= this.capacity) return false
    return (this.signatures[i * SIGNATURE_WORDS + type.word] & type.mask) !== 0
  }

  get<T>(e: Entity, type: ComponentType<T>): T | undefined {
    return this.store(type).get(e)
  }

  /** Use when a query already guarantees the component is present. */
  read<T>(e: Entity, type: ComponentType<T>): T {
    return this.store(type).getOrThrow(e)
  }

  // ------------------------------------------------------------------ queries

  query(all: readonly ComponentType<any>[], filter: QueryFilter = {}): Query {
    const key = queryKey(all, filter)
    let q = this.queries.get(key)
    if (!q) {
      for (const c of all) this.registerType(c)
      for (const c of filter.none ?? []) this.registerType(c)
      q = new Query(this, all, filter)
      this.queries.set(key, q)
    }
    return q
  }

  // ---------------------------------------------------------------- resources

  setResource<T>(key: ResourceKey<T>, value: T): T {
    this.resourceMap.set(key.name, value)
    return value
  }

  resource<T>(key: ResourceKey<T>): T {
    const v = this.resourceMap.get(key.name)
    if (v === undefined) throw new Error(`Resource "${key.name}" is not set`)
    return v as T
  }

  tryResource<T>(key: ResourceKey<T>): T | undefined {
    return this.resourceMap.get(key.name) as T | undefined
  }

  // ------------------------------------------------------------------ helpers

  /** Destroys every entity carrying `type` — used when tearing down a round. */
  destroyAllWith(type: ComponentType<any>): void {
    for (const e of [...this.store(type).owners]) this.destroyEntity(e)
    this.flushDestroyed()
  }

  private grow(minimum: number): void {
    let next = this.capacity
    while (next < minimum) next *= 2

    const sig = new Uint32Array(next * SIGNATURE_WORDS)
    sig.set(this.signatures)
    this.signatures = sig

    const gen = new Uint16Array(next)
    gen.set(this.generations)
    this.generations = gen

    const alive = new Uint8Array(next)
    alive.set(this.alive)
    this.alive = alive

    this.capacity = next
  }
}
